import { FC, useMemo, ReactElement, useCallback } from 'react'

import { useSimpleGuard } from 'helpers/loadableHelpers'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { CryptoRewardsPanePreset } from 'components/features/rewards/rewardsPane/components/CryptoRewardsPanePreset/CryptoRewardsPanePreset'
import { CryptoCurrencyId } from 'components/features/rewards/rewardsPane/types/cryptoCurrency'
import { CRYPTO_REWARDS_BUY_MODE_STEP_INVESTMENTS_AMOUNT } from 'components/features/rewards/rewardsPane/constants/paneSteps'
import { CryptoCurrencyLogo } from 'components/features/rewards/rewardsPane/containers/CryptoCurrencyLogo/CryptoCurrencyLogo'
import {
  getCryptoCurrenciesMetaDataWithMarketDataSelector,
  isCryptocurrenciesMetaListLoadingSelector,
} from 'components/features/rewards/rewardsPane/store/selectors/currencies'
import {
  CryptoRewardsCurrencyPickerWithMarketDetails,
} from 'components/features/rewards/rewardsPane/components/CryptoRewardsCurrencyPickerWithMarketDetails/CryptoRewardsCurrencyPickerWithMarketDetails'
import { getCryptoRewardsPaneProgressPercentsStateSelector } from 'components/features/rewards/rewardsPane/store/selectors/paneState'
import { cryptoMarketGuard } from 'components/features/rewards/rewardsPane/guards/cryptoCurrenciesMarketDataLoadedGuard'
import { FLOATING_PANE_CRYPTO_REWARDS_BUY_MODE } from 'components/features/rewards/rewardsPane/constants/paneState'
import { useCryptoRewardsPaneModifyStateCallback } from 'components/features/rewards/rewardsPane/hooks/useCryptoRewardsPaneModifyFieldsStateCallback'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'

const STEP_TITLE = 'Invest Your Rewards Into Crypto'
const STEP_SUBTITLE = 'Pick your crypto currency'


type CryptoRewardsBuyCurrencyListStepProps = {
  onClose: () => void
}

export const CryptoRewardsBuyCurrencyListStep: FC<CryptoRewardsBuyCurrencyListStepProps> = ({ onClose }) => {
  const isLoaded = useSimpleGuard(cryptoMarketGuard, {}, 'CryptoRewardsBuyCurrencyListStep')

  const paneProgressPercent = useStoreSelector(getCryptoRewardsPaneProgressPercentsStateSelector)

  const modifyPaneState = useCryptoRewardsPaneModifyStateCallback(FLOATING_PANE_CRYPTO_REWARDS_BUY_MODE)
  const handleCancelPressed = useCallback((): void => {
    modifyPaneState({
      step: CRYPTO_REWARDS_BUY_MODE_STEP_INVESTMENTS_AMOUNT,
    })
  }, [modifyPaneState])

  if (!isLoaded) {
    return (
      <CryptoRewardsPanePreset
        isLoading
        title={ STEP_TITLE }
        subTitle={ STEP_SUBTITLE }
        progressBarPercent={ paneProgressPercent }
        cancelName='Back'
        onCancel={ onClose }
        onFooterCancel={ handleCancelPressed }
      >
        <LoadingOverlay />
      </CryptoRewardsPanePreset>
    )
  }

  return (
    <CryptoRewardsBuyCurrencyListStepInner
      onClose={ onClose }
      onGoBack={ handleCancelPressed }
    />
  )
}


type CryptoRewardsBuyCurrencyListStepReadyProps = CryptoRewardsBuyCurrencyListStepProps & {
  onGoBack: () => void
}

const CryptoRewardsBuyCurrencyListStepInner: FC<CryptoRewardsBuyCurrencyListStepReadyProps> = ({
  onClose,
  onGoBack,
}) => {
  const paneProgressPercent = useStoreSelector(getCryptoRewardsPaneProgressPercentsStateSelector)
  const currenciesMeta = useStoreSelector(getCryptoCurrenciesMetaDataWithMarketDataSelector)
  const isMarketDataLoaded = useSimpleGuard(cryptoMarketGuard, {}, 'CryptoRewardsBuyCurrencyListStepReady')
  const isCryptoMetaLoading = useStoreSelector(isCryptocurrenciesMetaListLoadingSelector)
  const isPaneDataLoading = isCryptoMetaLoading || !isMarketDataLoaded

  const changeCryptoRewardsState = useCryptoRewardsPaneModifyStateCallback(FLOATING_PANE_CRYPTO_REWARDS_BUY_MODE)

  const getIconByCryptoId = useCallback((id: CryptoCurrencyId): ReactElement => <CryptoCurrencyLogo currencyId={ id } />, [])
  const handleCryptoCurrencyChosen = useCallback((id: CryptoCurrencyId) => {
    changeCryptoRewardsState({
      step: CRYPTO_REWARDS_BUY_MODE_STEP_INVESTMENTS_AMOUNT,
      fields: { cryptoCurrencyIDChosenForInvestment: id },
    })
  }, [changeCryptoRewardsState])
  const currenciesTable = useMemo<ReactElement>(() => (
    <CryptoRewardsCurrencyPickerWithMarketDetails
      isLoading={ isPaneDataLoading }
      getIconByCryptoId={ getIconByCryptoId }
      onChoose={ handleCryptoCurrencyChosen }
    >
      { currenciesMeta }
    </CryptoRewardsCurrencyPickerWithMarketDetails>
  ), [currenciesMeta, isPaneDataLoading, getIconByCryptoId, handleCryptoCurrencyChosen])

  return (
    <CryptoRewardsPanePreset
      title={ STEP_TITLE }
      subTitle={ STEP_SUBTITLE }
      progressBarPercent={ paneProgressPercent }
      onCancel={ onClose }
      onFooterCancel={ onGoBack }
    >
      { currenciesTable }
    </CryptoRewardsPanePreset>
  )
}
